import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Api from '../utils/Api';
// v0.19.22 — 后端错误文案的**共用入口** (三级兜底, 与本文件历史手抄版逐字等价)
import { errReason } from '../utils/FormPrimitives';
const COLOR_OPTIONS = ['var(--zs-brand-2d7be5, #2D7BE5)', 'var(--zs-success-34c759, #34c759)', 'var(--zs-purple-8b5cf6, #8b5cf6)', 'var(--zs-pink-ec4899, #ec4899)', 'var(--zs-warning-ffa726, #ffa726)', 'var(--zs-cyan-14b8a6, #14b8a6)', 'var(--zs-danger-ff6b6b, #ff6b6b)'];

/**
 * RoomEditorModal — 新建 / 编辑教室 (v0.5.0m)
 *
 * 跟 NewVersionModal / CardTemplateEditorModal 同款 pattern:
 *   - extends Modal (ModalManager 用 prototype instanceof Modal 检测单例)
 *   - 重写 className() / title() / content()
 *   - 保存后 this.hide() 走 Flarum 原生 ModalManager 关闭
 *
 * attrs:
 *   - room: null=新建, 教室对象=编辑
 *   - schoolId: 当前选中校区 (新建时自动归属)
 *   - onClose: 关闭后回调 (父 tab 刷新数据用)
 */
export default class RoomEditorModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);
    this.room = vnode.attrs.room;
    this.schoolId = vnode.attrs.schoolId;
    this.onCloseCb = vnode.attrs.onClose;
    this.saving = false;
    this.error = '';

    // 初始化表单 — 新建时空模板, 编辑时拷贝
    this.form = this.room ? {
      ...this.room
    } : {
      name: '',
      capacity: 8,
      color: 'var(--zs-brand-2d7be5, #2D7BE5)',
      status: 'enabled',
      school_id: this.schoolId ? +this.schoolId : null
    };
  }
  className() {
    // ⚠️ v0.19.31 — 补上专属 class `ZS-RoomEditorModal`。
    //   改前只有 'Modal--medium' ⇒ **无法**被 CSS 精确限定到本 modal
    //   (`.Modal--medium` 会命中全站所有中等宽度 modal)。
    //   v0.19.31 要给「所有 modal 的底部按钮」统一加 10px 间距 ⇒ 必须有专属 class。
    //   ⛔ 只**追加** class, 不改原有的 'Modal--medium' ⇒ 既有样式零影响。
    return 'Modal--medium ZS-RoomEditorModal';
  }
  title() {
    return this.room ? '编辑教室' : '新建教室';
  }
  validate() {
    if (!this.form.name || !String(this.form.name).trim()) return '请填写名称';
    const capacity = +this.form.capacity;
    if (!Number.isInteger(capacity) || capacity < 1) return '容量必须 ≥ 1 整数';
    return null;
  }
  async submit() {
    const err = this.validate();
    if (err) {
      this.error = err;
      m.redraw();
      return;
    }
    this.error = '';
    this.saving = true;
    m.redraw();
    try {
      const payload = {
        name: String(this.form.name).trim(),
        capacity: +this.form.capacity,
        color: this.form.color,
        status: this.form.status || 'enabled',
        school_id: this.form.school_id ? +this.form.school_id : this.schoolId ? +this.schoolId : null
      };
      if (this.room && this.room.id) {
        await Api.update('rooms', this.room.id, payload);
      } else {
        await Api.create('rooms', payload);
      }
      this.saving = false;
      if (this.onCloseCb) this.onCloseCb();
      this.hide();
    } catch (e) {
      this.saving = false;
      // v0.17.65 — 为什么不能只看 `e.message` (改前这里只有 `|| '保存失败'`)
      //   后端抛 ValidationException 时 HTTP 是 422, **真实原因在 `e.response.errors[0].detail`**;
      //   `e.message` 只是请求层拼的 "Response code 422 (Unprocessable Entity)", 跟后端那句提示无关。
      //   后果: 教室重名 / 校区不匹配这类**可操作**的原因被吞成「保存失败」, 用户不知道要改哪一项。
      //   形状照抄同仓既有正确写法 (CalendarTab.js:2770 / modal/FixedLessonModal.js:593);
      //   兜底文案「保存失败」保留不变, 只是降为最后一档 (网络层异常 / 非 422 时仍命中)。
      this.error = errReason(e, '保存失败');
      console.error('[room-editor] save failed', e);
      m.redraw();
    }
  }
  content() {
    return m("div", null, m("div", {
      className: "Modal-body"
    }, m("div", {
      className: "ZS-modal-form"
    }, m("div", {
      className: "ZS-form-row"
    }, m("label", null, "\u540D\u79F0"), m("input", {
      className: "ZS-form-input",
      value: this.form.name,
      oninput: ev => {
        this.form.name = ev.target.value;
        m.redraw();
      }
    })), m("div", {
      className: "ZS-form-row"
    }, m("label", null, "\u5BB9\u91CF"), m("input", {
      type: "number",
      className: "ZS-form-input",
      value: this.form.capacity,
      oninput: ev => {
        this.form.capacity = +ev.target.value;
        m.redraw();
      }
    })), m("div", {
      className: "ZS-form-row"
    }, m("label", null, "\u989C\u8272"), m("div", {
      style: "display:flex;gap:8px;flex-wrap:wrap;"
    }, COLOR_OPTIONS.map(c => m("div", {
      key: c,
      style: 'width:32px;height:32px;border-radius:6px;background:' + c + ';cursor:pointer;border:2px solid ' + (this.form.color === c ? 'white' : 'transparent') + ';',
      onclick: () => {
        this.form.color = c;
        m.redraw();
      }
    })))), this.error && m("div", {
      className: "ZS-newversion-error",
      style: "margin-top:8px;"
    }, m("i", {
      className: "fas fa-triangle-exclamation",
      style: "color:var(--zs-danger-ef4444, #ef4444);margin-right:6px;"
    }), this.error))), m("div", {
      className: "Modal-footer"
    }, m("button", {
      className: "Button",
      onclick: () => this.hide()
    }, "\u53D6\u6D88"), m("button", {
      className: "Button Button--primary",
      disabled: this.saving,
      onclick: () => this.submit()
    }, this.saving ? '保存中...' : '保存')));
  }
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/modal/RoomEditorModal', RoomEditorModal);